<template>
    <template v-if="loading">
        <ScaleLoader class="custom-loader" height="5rem" />
    </template>
    <template v-else>
        <div class="grid">
            <div class="flex flex-column col-3 field">

                <label class="mb-2" for="theme_id">Theme</label>
                <VueTreeSelect
                    v-if="!learningOutcomeData.complete"
                    id="theme_id"
                    class="p-multiselect p-component"
                    v-model="learningOutcomeData.theme_id"
                    v-on:update:modelValue="onDataUnsaved"
                    :options="themes"
                    placeholder="Select a Theme"
                    value-consists-of="ALL"
                    :multiple="false"
                />
                <div v-else>
                    <b>{{learningOutcomeData?.theme?.name}}</b>
                </div>

            </div>
            <div class="flex flex-column col-3 field">

                <label class="mb-2" for="theme_id">Source</label>
                <VueTreeSelect
                    v-if="!learningOutcomeData.complete"
                    id="theme_id"
                    class="p-multiselect p-component"
                    v-model="learningOutcomeData.source_id"
                    v-on:update:modelValue="onDataUnsaved"
                    :options="sources"
                    placeholder="Select a Source"
                    value-consists-of="ALL"
                    :multiple="false"
                />
                <div v-else>
                    <b>{{learningOutcomeData?.source?.name}}</b>
                </div>

            </div>

            <div class="flex flex-column col-3 field">
                <label class="mb-2" for="incident_category">Type</label>
                <Dropdown
                    v-if="!learningOutcomeData.complete"
                    id="incident_category"
                    v-model="learningOutcomeData.outcome_type"
                    :options="outcomeTypes"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select a Type"
                    class="mb-1"
                    :showClear="true"
                    v-on:update:modelValue="onDataUnsaved"
                />
                <div v-else>
                    <b>{{selectedOutcomeType}}</b>
                </div>
            </div>

            <div class="flex flex-column col-1 field" 
            v-if="!learningOutcomeData.complete && 
                authStore.hasPermission(UserPermission.WRITE_LEARNING_OUTCOME)">
                <label class="mb-2" for="incident_category">Complete</label>
                <InputSwitch v-model="learningOutcomeData.complete"
                             v-on:update:modelValue="onDataUnsaved"
                             :disabled="!learningOutcomeData.id" />
            </div>
            <div class="flex flex-column col-2 field" v-if="!learningOutcomeData.complete">
                <small>
                    <b>Learning Outcomes can be set as complete once created.</b>
                </small>
            </div>

            <div class="flex flex-column col-3 field" v-if="learningOutcomeData.complete">
                <h6 class="mt-5">Marked as completed - <b>Press submit below.</b></h6>
            </div>

            <a v-if="themeHelpLink && enable_theme_help_resource_links" target="_blank" :href="themeHelpLink" style="margin-left: 20px">
                Theme selection guide
            </a>

            <div class="flex flex-column col-12 field">
                <label class="mb-2" for="details">Details</label>
                <BaseEditor
                    v-model="learningOutcomeData.details"
                    v-if="!learningOutcomeData.complete"
                    v-on:update:modelValue="onDataUnsaved"
                />

                <div v-if="learningOutcomeData.complete" v-html="learningOutcomeData.details" class="learningOutcomeDetailHtml" />
            </div>

            <div class=" col-12 justify-content-center" >
                <FileUpload
                    class="fileUploader"
                    @uploader="onFileUpload"
                    @select="onSelectedFiles"
                    :multiple="true"
                    :maxFileSize="5000000"
                    :accept="acceptedFormats"
                    :custom-upload="true"
                    auto
                >
                    <template #header="{ chooseCallback }">
                        <div class="flex flex-wrap justify-content-between align-items-center flex-1 gap-2">
                            <div class="flex gap-2">
                                <Button @click="chooseCallback()" icon="pi pi-file" rounded outlined label="Choose"></Button>
                                <!--
                                <Button @click="uploadEvent(uploadCallback)" icon="pi pi-cloud-upload" label="Upload" rounded outlined severity="success" :disabled="!files || files.length === 0"></Button>
                                <Button @click="clearCallback()" icon="pi pi-times" rounded outlined severity="danger" label="Clear" :disabled="!files || files.length === 0"></Button>
                                -->
                            </div>
                        </div>
                    </template>
                    <template #content="{ files, uploadedFiles, removeUploadedFileCallback, removeFileCallback }">
                        <div v-if="files.length > 0">
                            <h5>Pending</h5>
                            <div class="flex flex-wrap p-0 sm:p-5 gap-5">
                                <div v-for="(file, index) of files" :key="file.name + file.type + file.size" class="card m-0 px-6 flex flex-column border-1 surface-border align-items-center gap-3">
                                    <div>
                                        <img role="presentation" :alt="file.name" :src="file.objectURL" width="100" height="50" />
                                    </div>
                                    <span class="font-semibold">{{ file.name }}</span>
                                    <div>{{ formatSize(file.size) }}</div>
                                    <Badge value="Pending" severity="warning" />
                                    <Button icon="pi pi-times" @click="onRemoveTemplatingFile(file, removeFileCallback, index)" outlined rounded  severity="danger" />
                                </div>
                            </div>
                        </div>

                        <div v-if="uploadedFiles.length > 0">
                            <h5>Completed</h5>
                            <div class="flex flex-wrap p-0 sm:p-5 gap-5">
                                <div v-for="(file, index) of uploadedFiles" :key="file.name + file.type + file.size" class="card m-0 px-6 flex flex-column border-1 surface-border align-items-center gap-3">
                                    <div>
                                        <img role="presentation" :alt="file.name" :src="file.objectURL" width="100" height="50" />
                                    </div>
                                    <span class="font-semibold">{{ file.name }}</span>
                                    <div>{{ formatSize(file.size) }}</div>
                                    <Badge value="Completed" class="mt-3" severity="success" />
                                    <Button icon="pi pi-times" @click="removeUploadedFileCallback(index)" outlined rounded  severity="danger" />
                                </div>
                            </div>
                        </div>
                    </template>
                    <template #empty>
                        <div class="flex align-items-center justify-content-center flex-column">
                            <i class="pi pi-cloud-upload border-2 border-circle p-5 text-8xl text-400 border-400" />
                            <p class="mt-4 mb-0">Drag and drop files to here to upload.</p>
                        </div>
                    </template>
                </FileUpload>
            </div>

            <div class="col-12">

                <DataTable
                    :value="learningOutcomeData.attachments"
                    :paginator="true"
                    :rows="5"
                    dataKey="id"
                    :totalRecords="learningOutcomeData.attachments.length"
                    :loading="savingDebrief || learningOutcomeSaving"
                    responsiveLayout="scroll"
                    :rowsPerPageOptions="[5, 10, 20, 50, 100]"
                    paginatorTemplate="CurrentPageReport JumpToPageInput FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries :: Page No:"
                >
                    <template #empty> No Attachments Available. </template>

                    <Column field="caption" header="Caption"  />
                    <Column field="description" header="Description"  />
                    <Column field="original_name" header="File name"  />
                    <Column field="extension" header="Extension (file type)">
                        <template #body="slotProps">
                            <span v-if="slotProps.data.extension && slotProps.data.extension.length > 0">{{slotProps.data.extension}}</span>
                            <span v-else style="color: rgb(200, 0, 0)">Not linked with LO yet</span>
                        </template>
                    </Column>
                    <Column header="Download">
                        <template #body="slotProps">
                            <a @click.prevent @click="download(slotProps.data)" style="cursor: pointer">
                                <i class="pi pi-download" style="font-size: 1.5rem" v-tooltip="'Download the file'"></i>
                            </a>


                        </template>
                    </Column>
                    <Column field="actions" header="ACTIONS" style="min-width: 8rem">
                        <template #body="slotProps">
                            <Button v-if="authStore.hasPermission(UserPermission.WRITE_LEARNING_OUTCOME)" icon="pi pi-pencil" class="p-button-rounded p-button-info mr-2" @click="editAttachment(slotProps.data)" v-tooltip="'Amend caption and description'" />
                            <Button v-if="authStore.hasPermission(UserPermission.WRITE_LEARNING_OUTCOME)" icon="pi pi-trash" class="p-button-rounded p-button-danger" @click="confirmRequest(slotProps.data, removeAttachment)" v-tooltip="'Remove Attached file from the Learning Outcome'" />
                        </template>
                    </Column>
                </DataTable>
            </div>

            <div class="flex col-12 justify-content-between">
                <div>
                    <div v-if="unsavedData === true">
                        There are changes made that aren't saved yet. Click <b>Submit</b> to record them.
                    </div>
                </div>
                <div class="flex justify-content-end ">
                    <Button
                        v-if="debrief && learningOutcomeData.id && authStore.hasPermission(UserPermission.WRITE_TASK)"
                        :loading="savingDebrief || learningOutcomeSaving"
                        :disabled="savingDebrief || learningOutcomeSaving || learningOutcomeData.complete"
                        icon="pi pi-briefcase"
                        label="Add Task"
                        severity="info"
                        @click="onAddTask"
                        class="mr-2"
                    />

                    <Button
                        v-if="authStore.hasPermission(UserPermission.WRITE_LEARNING_OUTCOME)"
                        :loading="savingDebrief || learningOutcomeSaving"
                        :disabled="savingDebrief || learningOutcomeSaving || (learningOutcomeData.complete && !unsavedData)"
                        icon="pi pi-save"
                        label="Submit"
                        severity="success"
                        @click="onSave"
                        class="mr-2"
                    />
                    <Button
                        v-if="popupMode"
                        :loading="savingDebrief || learningOutcomeSaving"
                        :disabled="savingDebrief || learningOutcomeSaving"
                        label="Close"
                        icon="pi pi-times"
                        severity="warning"
                        @click="closeLearningOutcomePopup"
                    />
                </div>
            </div>

            <div class="col-12">
                <Accordion>
                    <AccordionTab v-if="entityLogIds.length > 0" header="Events">
                        <LogViewer :event_type="logEventTypes" :entity_ids="entityLogIds"/>
                    </AccordionTab>
                </Accordion>
            </div>

        </div>

        <Dialog v-model:visible="editAttachmentDetailsVisible" modal header="Edit Attachment" :style="{ width: '25rem' }">
            <div class="flex align-items-center gap-3 mb-2">
                <label for="caption" class="font-semibold w-6rem">Caption</label>
                <InputText v-model="attachmentDetails.caption" id="caption" class="flex-auto" autocomplete="off" />
            </div>

            <div class="flex align-items-center gap-3 mb-2">
                <label for="description" class="font-semibold w-6rem">Description</label>
                <InputText v-model="attachmentDetails.description" id="description" class="flex-auto" autocomplete="off" />
            </div>

            <template #footer>
                <Button
                    icon="pi pi-save"
                    label="Submit"
                    severity="success"
                    @click="editAttachmentConfirm"
                    class="mr-2"
                />
                <Button
                    label="Close"
                    icon="pi pi-times"
                    severity="warning"
                    @click="editAttachmentDetailsVisible = false"
                />
            </template>
        </Dialog>
    </template>
</template>

<script setup>

import { computed, defineEmits, defineProps, onMounted, ref, watch } from 'vue';
import { authApi } from '@/utilities/axios';
import { authStore } from '@/store/global-stores';
import { showToast } from '@/utilities/toast-handler';
import { usePrimeVue } from 'primevue/config';
import { useConfirmRemove } from '@/composables/ConfirmComposable';
import { useRouter } from 'vue-router';
import LogViewer from '@/product/ams/components/LogViewer.vue';
import { UserPermission } from '@/modules/permission/enum';
import { useLabels } from '@/composables/LabelComposable';
import { userCompanyPreference } from '@/composables/CompanyPreferenceComposable';



const $primevue = usePrimeVue();
const router = useRouter();
const { label } = useLabels();

const { fetchCompanyPreference } = userCompanyPreference();

const { confirmRequest } = useConfirmRemove();

const acceptedFormats =
    'image/*,.pptx, .xlsx, .xltx, .docx, .ppt, .xls, .xlt, .doc, .eml, .jpeg, .jpg, .jpe, .png, .gif, .tif, .tiff, .csv, .rtf, .pdf, .odc, .otc, .odb, .odf, .odft, .odg, .otg, .odi, .oti, .odp, .otp, .ods, .ots, .odt, .odm, .ott, .oth, .dwg, .msg';



const entityLogIds = ref([])

const logEventTypes = [
    'learning_outcome',
]

const emit = defineEmits(['close_popup', 're-load-debrief']);

const company_id = computed(() => {
    return authStore.getLoggedUserCompanyId;
});

const learningOutcomeSaving = ref(false)

const themeHelpLink = ref(null)

function closeLearningOutcomePopup() {
    emit('close_popup', true)
}

const props = defineProps({
    learningOutcome: {
        type: [Number, String],
        required: false,
    },
    themes: {
        type: [Array],
        required: true,
    },
    sources: {
        type: [Array],
        required: true,
    },
    popupMode: {
        type: Boolean,
        required: false,
        default: false
    },
    savingDebrief: {
        type: Boolean,
        required: false,
        default: false
    },
    debriefId: {
        type: [Number, String],
        required: false,
    },
    debrief: {
        type: Object,
        required: false,
    }
});

const outcomeTypes = [
    {
        value: 1,
        label: 'Local Issue',
    },
    {
        value: 2,
        label: 'Service Wide',
    },
    {
        value: 3,
        label: 'Notable Practice',
    },
]


const selectedOutcomeType = computed(() => {
    const selectedOutcomeType = outcomeTypes.filter((item) => item.value === learningOutcomeData.value.outcome_type)

    if (selectedOutcomeType.length === 1) {
        return selectedOutcomeType[0].label
    } else {
        return ''
    }
});

const loading = ref(false)

const learningOutcomeStart = {
    id: null,
    debrief_id: null,
    company_id: company_id.value,
    theme: null,
    theme_id: null,
    source: null,
    source_id: null,
    outcome_type: null,
    details: null,
    complete: false,
    send_to_rol_outcome: null,
    send_to_rol_at: null,
    send_to_nol_outcome: null,
    send_to_nol_at: null,
    send_to_jol_outcome: null,
    send_to_jol_at: null,
    attachments: [],
    incident: null
}

const editAttachmentDetailsVisible = ref(false)
const attachmentDetails = ref({
    id: null,
    caption: '',
    description: '',
})

const learningOutcomeData = ref(learningOutcomeStart)
const unsavedData = ref(false)

function onDataUnsaved() {
    unsavedData.value = true
    // console.log('unsavedData.value = true')
}

onMounted(() => {
    // console.log('onMounted in CreateUpdateLearningOutcome.vue - learningOutcome:', props.learningOutcome)
    // console.log('onMounted in CreateUpdateLearningOutcome.vue - debriefId', props.debriefId)

    entityLogIds.value = []

    if (props.learningOutcome && typeof props.learningOutcome !== 'undefined') {
        loadLearningOutcome(props.learningOutcome)
    } else {
        learningOutcomeData.value = learningOutcomeStart
        learningOutcomeData.value.debrief_id = props.debriefId
        learningOutcomeData.value.complete = 0

        entityLogIds.value.push(props.debriefId)
    }

    loadThemeHelpLink()
});

function loadCompanyPreference() {
    if (company_id.value && company_id.value > 0) {
        fetchCompanyPreference(company_id.value).then((prefs) => {
            enable_theme_help_resource_links.value = prefs?.enable_theme_help_resource_links
        });
    }
}

watch(company_id, loadCompanyPreference);
loadCompanyPreference();

function loadThemeHelpLink() {
    authApi
        .get(`/v2/admin/company/${company_id.value}/theme-help-link`)
        .then((response) => {
            // existing value normalized in normalizeUrl()

            themeHelpLink.value = normalizeUrl(response.data?.value)

        })
        .catch(() => {
            themeHelpLink.value = null
        })
}

function normalizeUrl(url) {
    if (!url) return null

    let candidate = String(url).trim()
    if (!/^https?:\/\//i.test(candidate)) {
        candidate = `https://${candidate}`
    }

    try {

        return new URL(candidate).toString()
    } catch {
        return null
    }
}

function download(data) {
    const fileId = data.id
    const url = `v2/download/${fileId}?company_id=${company_id.value}`;
    authApi.get(url, { responseType: 'blob' }).then(response => {
        // console.log('response', response)
        // console.log('fileName', data.original_name)

        const blob = new Blob([response.data], { type: response.type })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = data.original_name
        link.click()
        URL.revokeObjectURL(link.href)
    }).catch(error => {
        console.log('Error: ', error)
    })
}

function editAttachment(data) {
    // console.log('editAttachment', data)

    attachmentDetails.value.id = data.id
    attachmentDetails.value.caption = data.caption
    attachmentDetails.value.description = data.description

    editAttachmentDetailsVisible.value = true
}

function editAttachmentConfirm() {
    // console.log('editAttachment', attachmentDetails.value)

    const index = learningOutcomeData.value.attachments.findIndex(item => item.id === attachmentDetails.value.id)

    learningOutcomeData.value.attachments[index].caption = attachmentDetails.value.caption
    learningOutcomeData.value.attachments[index].description = attachmentDetails.value.description

    editAttachmentDetailsVisible.value = false
}

function removeAttachment(id) {
    // console.log('id', id)

    const index = learningOutcomeData.value.attachments.findIndex(item => item.id === id)

    if (index > -1) { // only splice array when item is found
        learningOutcomeData.value.attachments.splice(index, 1); // 2nd parameter means remove one item only
    }
}

function onFileUpload(event) {
    learningOutcomeSaving.value = true
    unsavedData.value = true

    let filesSaved = 0

    if (typeof event !== 'undefined') {
        if (Object.hasOwn(event, 'files') && Array.isArray(event.files)) {
            // console.log(event.files[0])

            const formData = new FormData();
            formData.append('file', event.files[0]);

            event.files.forEach((file) => {
                // console.log('file', file)

                const formData = new FormData();
                formData.append('file', file);

                authApi.post(`/v2/upload?company_id=${company_id.value}`, formData, { headers: {
                        'Content-Type': 'multipart/form-data',
                    }
                }).then(response => {
                    // console.log(response)

                    if (response.statusCode === 201) {

                        // console.log('debrief saved (patch)', response)

                        if (Object.hasOwn(response, 'data')) {
                            filesSaved++

                            // console.log('filesSaved', filesSaved)
                            // console.log('event.files.length', event.files.length)

                            if (filesSaved >= event.files.length) {
                                learningOutcomeSaving.value = false
                            }

                            learningOutcomeData.value.attachments.push({
                                id: response.data.id,
                                caption: response.data.original_name,
                                description: response.data.original_name,
                            })
                        }
                    }
                }).catch((error) => {
                    showToast(error, 'Failed to save the file ');
                })
            })
        }
    }
}

function onSave() {
    saveLearningOutcome().then(() => {
        emit('re-load-debrief')
    })
}

function saveLearningOutcome() {

    learningOutcomeSaving.value = true

    if (learningOutcomeData.value.id && learningOutcomeData.value.id > 0) {
        return authApi
            .patch(`/v2/learning-outcome/${learningOutcomeData.value.id}`, learningOutcomeData.value)
            .catch((error) => {
                learningOutcomeSaving.value = false
                showToast(error, `Failed to save the ${label('Debrief').toLowerCase()}`);
            })
            .then((response) => {
                if (response.statusCode === 200) {
                    learningOutcomeSaving.value = false

                    parseLearningOutcomeAnswer(response)
                }
            })
    } else {
        return authApi
            .post(`/v2/learning-outcome`, learningOutcomeData.value)
            .catch((error) => {
                learningOutcomeSaving.value = false
                showToast(error, `Failed to save the ${label('Debrief').toLowerCase()}`);
            })
            .then((response) => {
                if (response.statusCode === 201) {
                    learningOutcomeSaving.value = false

                    parseLearningOutcomeAnswer(response)
                }
            })
    }
}

function parseLearningOutcomeAnswer(response) {
    if (Object.hasOwn(response, 'data')) {

        learningOutcomeData.value.debrief_id = props.debriefId

        if (Object.hasOwn(response.data, 'id')) {
            learningOutcomeData.value.id = response.data.id
            entityLogIds.value.push(response.data.id)
        }
        if (Object.hasOwn(response.data, 'debrief_id')) {
            learningOutcomeData.value.debrief_id = response.data.debrief_id
            entityLogIds.value.push(response.data.debrief_id)
        }
        if (Object.hasOwn(response.data, 'complete')) {
            learningOutcomeData.value.complete = parseInt(response.data.complete) === 1
        }
        if (Object.hasOwn(response.data, 'theme')) {
            learningOutcomeData.value.theme = response.data.theme
        }
        if (Object.hasOwn(response.data, 'theme_id')) {
            learningOutcomeData.value.theme_id = response.data.theme_id
        }
        if (Object.hasOwn(response.data, 'source')) {
            learningOutcomeData.value.source = response.data.source
        }
        if (Object.hasOwn(response.data, 'source_id')) {
            learningOutcomeData.value.source_id = response.data.source_id
        }
        if (Object.hasOwn(response.data, 'details')) {
            learningOutcomeData.value.details = response.data.details
        }
        if (Object.hasOwn(response.data, 'outcome_type')) {
            learningOutcomeData.value.outcome_type = parseInt(response.data.outcome_type)
        }
        if (Object.hasOwn(response.data, 'send_to_rol_outcome')) {
            learningOutcomeData.value.send_to_rol_outcome = response.data.send_to_rol_outcome
        }
        if (Object.hasOwn(response.data, 'send_to_rol_at')) {
            learningOutcomeData.value.send_to_rol_at = response.data.send_to_rol_at
        }
        if (Object.hasOwn(response.data, 'send_to_nol_outcome')) {
            learningOutcomeData.value.send_to_nol_outcome = response.data.send_to_nol_outcome
        }
        if (Object.hasOwn(response.data, 'send_to_nol_at')) {
            learningOutcomeData.value.send_to_nol_at = response.data.send_to_nol_at
        }
        if (Object.hasOwn(response.data, 'send_to_jol_outcome')) {
            learningOutcomeData.value.send_to_jol_outcome = response.data.send_to_jol_outcome
        }
        if (Object.hasOwn(response.data, 'send_to_jol_at')) {
            learningOutcomeData.value.send_to_jol_at = response.data.send_to_jol_at
        }

        if (Object.hasOwn(response.data, 'author')) {
            learningOutcomeData.value.author = response.data.author
        }

        if (Object.hasOwn(response.data, 'attachments')) {
            learningOutcomeData.value.attachments = response.data.attachments
            //console.log('response.data.attachments', response.data.attachments)

            learningOutcomeData.value.attachments = []

            response.data.attachments.forEach(attachment => {
                /**
                 {
                 "id": 2515,
                 "caption": "Caption 10",
                 "description": "Description 10"
                 },
                 */
                // console.log('attachment', attachment)

                learningOutcomeData.value.attachments.push({
                    id: attachment.pivot.attachment_id,
                    caption: attachment.caption,
                    description: attachment.description,
                    extension: attachment.extension,
                    original_name: attachment.original_name,
                })
            })
        }
    }
}

function loadLearningOutcome(learningOutcomeId) {
    // console.log('loadLearningOutcome', learningOutcomeId)

    if (learningOutcomeId && typeof learningOutcomeId !== 'undefined') {
        loading.value = true
        authApi.get(`/v2/learning-outcome/${learningOutcomeId}?company_id=${company_id.value}`)
            .then( async (response) => {
                loading.value = false

                if (response.statusCode === 200) {
                    parseLearningOutcomeAnswer(response)
                }
            }).catch((error) => {
                console.error(error)
                showToast(error, 'Failed to load the Learning Outcome');
            });
    }
}

const totalSize = ref(0);
const totalSizePercent = ref(0);
const files = ref([]);
const enable_theme_help_resource_links = ref(false);

const onRemoveTemplatingFile = (file, removeFileCallback, index) => {
    removeFileCallback(index);
    totalSize.value -= parseInt(formatSize(file.size));
    totalSizePercent.value = totalSize.value / 10;
};

/*
const onClearTemplatingUpload = (clear) => {
    clear();
    totalSize.value = 0;
    totalSizePercent.value = 0;
};
*/
const onSelectedFiles = (event) => {

    // console.log('onSelectedFiles', event)

    files.value = event.files;
    files.value.forEach((file) => {
        totalSize.value += parseInt(formatSize(file.size));
    });
};

const formatSize = (bytes) => {
    const k = 1024;
    const dm = 3;
    const sizes = $primevue.config.locale.fileSizeTypes;

    if (bytes === 0) {
        return `0 ${sizes[0]}`;
    }

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const formattedSize = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

    return `${formattedSize} ${sizes[i]}`;
};


function onAddTask() {
    if (typeof props.debrief !== 'undefined' && typeof props.debrief.incident !== 'undefined') {
        saveLearningOutcome().then(() => {
            // console.log('onAddTask', props.debrief.incident.incident_number)
        })
    }


    router.push({
        name: 'TaskForm',
        query: {
            debriefId: learningOutcomeData.value.debrief_id,
            learningOutcomeId: learningOutcomeData.value.id,
            outcomeType: outcomeTypes.find((outcome) => outcome.value === learningOutcomeData.value.outcome_type)?.label,
            incidentNo: props.debrief.incident.incident_number,
            source: learningOutcomeData.value.source_id,
            theme: learningOutcomeData.value.theme_id,
            details: learningOutcomeData.value.details,
            author: learningOutcomeData.value.author.name ?? 'none',
        },
    })
}

</script>

<style scoped>
/*
    Override CSS to remove the unwanted border around the tree control
 */
.vue-treeselect {
    border: none !important;
}
.vue-treeselect__placeholder {
    color: rgb(0,0,0)  !important;
}
.vue-treeselect__single-value {
    color: rgb(0,0,0)  !important;
}
.vue-treeselect--focused.vue-treeselect--searchable .vue-treeselect__single-value {
    color: rgb(0,0,0)  !important;
}

.learningOutcomeDetailHtml {
    padding: 10px;
    border-width: 1px;
    border-color: rgb(220,220,220);
    border-style: solid;
}

.p-fileupload-content {
    width: 500px !important;
}

::v-deep(.vue-treeselect__placeholder) {
    color: #657379 !important;
}
</style>